import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Context } from '../controls/advanced-gridview/models/context';
import { FilterInfos } from '../controls/advanced-gridview/models/filter-infos';
import { Results } from '../controls/advanced-gridview/models/results-T';
import { SortInfos } from '../controls/advanced-gridview/models/sort-infos';
import { AdvancedService } from '../controls/advanced-gridview/services/advanced-service';
import { Organization } from '../models/organization';
import { CrudService } from './crud-service';
import { MemoryService } from './memory.service';

@Injectable({
    providedIn: 'root',
})
export class OrganizationsService extends CrudService implements AdvancedService<Organization> {
    /**
     * Liste des organisations autorisées à accéder à Hub4B
     */
    private organisationsAllowedToAccessHub4B = new BehaviorSubject<Organization[]>(null);

    /**
     * Propriété retournant la liste des organisations autorisées à accéde à Hub4B
     */
    public get organisationsAllowedToAccessHub4BChange$() {
        return this.organisationsAllowedToAccessHub4B.asObservable();
    }

    constructor(
        private http: HttpClient,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    ListChildren(organizationId: string, deepSearch: boolean): Observable<Organization[]> {
        // Appel de la Web Api
        return this.http.get<Organization[]>(this.baseUrl + `/api/Organizations/${organizationId}/children?deepSearch=${deepSearch}`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                var res = Organization.fromArray(x);
                return res;
            })
        );
    }

    List(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos, pageNumber = 0, pageSize = 100): Observable<Results<Organization>> {
        let extendedFilters: FilterInfos[] = [...filters];
        let extendedGeneralFilters: FilterInfos[] = [...generalFilters];

        let params = new Map<string, string | number | boolean>();
        params.set('from', pageNumber);
        params.set('size', pageSize);
        params.set('activated', true);

        let httpOptions = this.getListHttpOptions(context, extendedFilters, extendedGeneralFilters, sortInfos, params);

        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations`, httpOptions).pipe(
            map((x) => {
                var res = Results.getCopyFrom<Organization>(Organization, x);

                return res;
            })
        );
    }

    Export(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos): Observable<Blob> {
        const headers = this.getExportHeader(context, filters, generalFilters, sortInfos);

        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/export`, { headers: headers, responseType: 'blob' }).pipe(
            map((res: any) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }

    delete(organizationId: string): Observable<boolean> {
        // Appel de la Web Api
        return this.http.delete<Organization>(this.baseUrl + `/api/Organizations/${organizationId}`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                let organizations = this.organisationsAllowedToAccessHub4B.value;
                let index = organizations.findIndex((x) => x.id === organizationId);
                organizations.splice(index, 1);
                this.organisationsAllowedToAccessHub4B.next(
                    organizations.sort((a, b) => {
                        return a.name < b.name ? -1 : 1;
                    })
                );
                return true;
            })
        );
    }

    // add(organization: Organization): Observable<Organization> {
    //   // Appel de la Web Api
    //   return this.http.post<Organization>(this.baseUrl + `/api/Organizations`, organization, this.getDefaultHttpOptions()).pipe(map(x => {
    //     return x;
    //   }));
    // }

    /**
     * Active l'accès à Hub4B pour  une organisation
     * @param organizationId : Id de l'organisation à activer.
     */
    activate(organizationId: string): Observable<void> {
        // Appel de la Web Api
        return this.http.post<Organization>(this.baseUrl + `/api/Organizations/${organizationId}`, null).pipe(
            map((x) => {
                let organizations = this.organisationsAllowedToAccessHub4B.value;
                this.get(organizationId).subscribe((org) => {
                    console.log('organisation activée à ajouter: ', org);
                    organizations.push(org);
                    this.organisationsAllowedToAccessHub4B.next(
                        organizations.sort((a, b) => {
                            return a.name < b.name ? -1 : 1;
                        })
                    );
                });
            })
        );
    }

    /**
     * Désactive l'accès à Hub4B pour  une organisation
     * @param organizationId : Id de l'organisation à activer.
     */
    desactivate(organizationId: string): Observable<void> {
        // Appel de la Web Api
        return this.http.patch<Organization>(this.baseUrl + `/api/Organizations/${organizationId}`, null).pipe(
            map((x) => {
                let organizations = this.organisationsAllowedToAccessHub4B.value;
                let index = organizations.findIndex((x) => x.id === organizationId);
                organizations.splice(index, 1);
                this.organisationsAllowedToAccessHub4B.next(
                    organizations.sort((a, b) => {
                        return a.name < b.name ? -1 : 1;
                    })
                );
            })
        );
    }

    /**
     * Active l'archivage pour une organisation.
     * @param organizationId : Id de l'organisation
     * @returns L'instance de l'organisation.
     */
    activateArchiving(organizationId: string): Observable<Organization> {
        // Appel de la Web Api
        return this.http.patch<Organization>(this.baseUrl + `/api/organizations/${organizationId}/archiving`, null, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                let organizations = this.organisationsAllowedToAccessHub4B.value;
                let index = organizations.findIndex((x) => x.id === organizationId);
                organizations[index].archivingEnabled = x.archivingEnabled;
                organizations[index].updateDate = x.updateDate;
                return x;
            })
        );
    }

    get(organizationId: string): Observable<Organization> {
        // Appel de la Web Api
        return this.http.get<Organization>(this.baseUrl + `/api/Organizations/${organizationId}`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return Organization.Map(x);
            })
        );
    }

    /**
     * Retourne la liste des organisations autorisées à accéder à Hub4B à partir d'une organisation parente.
     * @param organization Organisation parente.
     * @returns Liste des organisations autorisées à accéder à Hub4B
     */
    getOrganisationsAllowedToAccessHub4B(organization: Organization): Observable<Organization[]> {
        return this.ListChildren(organization.id, true).pipe(
            map((organizations) => {
                let organizationsAllowed = organizations.filter((a) => a.isAllowedToAccessHub4B === true);
                organizationsAllowed.push(organization);
                this.organisationsAllowedToAccessHub4B.next(
                    organizationsAllowed.sort((a, b) => {
                        return a.name < b.name ? -1 : 1;
                    })
                );

                return organizationsAllowed;
            })
        );
    }
}
